/**
 * 24-Hour Line app (feature_key 'oncall'; docs/plans/oncall-line-plan.md 7.5,
 * 7.7; WP-03), copying the Learning Center shell (lms/LmsApp.tsx).
 *
 * Dark until the launch runbook (plan 7.2): the features row ships
 * is_active=false and nobody holds the entitlement, so every non-super_admin
 * sees "Access Denied" here. Access is the site's oncall entitlement
 * (GET /api/my-tools, tool_id 'oncall', is_available === true), super_admin
 * bypassing it; every /api/oncall route re-checks it server-side.
 *
 * Tabs: Calls (the index, for the whole on-call team, plan Q8), Team and
 * Settings (line admins only). A line admin is a super_admin, a platform
 * administrator, or tool_user_roles (oncall, admin) at the site (plan 7.5);
 * the shell takes the server's own verdict from GET /api/oncall/health
 * (is_admin) only to show or hide the admin tabs, since every admin route
 * carries requireOncallAdmin server-side anyway.
 *
 * The entitlement check and the Layout sit ABOVE <Routes> so the shell mounts
 * once per module visit. Both the entitlement and the admin role are per
 * SITE, so both re-run on a site switch and ignore an answer for a site that
 * is no longer current; switching site switches line (plan 7.7).
 *
 * Routes (the tab files keep a default export with no required props):
 *   /oncall                 CallsTab (WP-12)
 *   /oncall/calls/:callId   CallsTab too: the alert emails' link (plan WP-09),
 *                           read with useParams().callId to open the drawer
 *   /oncall/team            TeamTab (WP-11), admin only
 *   /oncall/settings        SettingsTab (WP-11), admin only
 *   anything else           back to /oncall
 *
 * No module provider and no SSE: the tabs poll (plan 7.7). ToastProvider is
 * mounted once here so every screen can call useToast.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, Building2, ChevronDown, LogOut, PhoneCall, SlidersHorizontal, User, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SiteSelector from '../components/SiteSelector';
import AppBar from '../ui/AppBar';
import EmptyState from '../ui/EmptyState';
import { ToastProvider } from '../ui/Toast';
import { APP_VERSION, BUILD_DATE, COPYRIGHT_YEAR, COMPANY_NAME } from '../config/version';
import { apiErrorMessage, getOncallHealth } from './api/oncallApi';
import CallsTab from './tabs/CallsTab';
import TeamTab from './tabs/TeamTab';
import SettingsTab from './tabs/SettingsTab';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

type OncallTabKey = 'calls' | 'team' | 'settings';

interface OncallTab {
  key: OncallTabKey;
  /** The tab's href. */
  href: string;
  label: string;
  icon: LucideIcon;
  /** Team and Settings show only for a line admin (plan 7.5). */
  adminOnly: boolean;
}

const ONCALL_TABS: OncallTab[] = [
  { key: 'calls', href: '/oncall', label: 'Calls', icon: PhoneCall, adminOnly: false },
  { key: 'team', href: '/oncall/team', label: 'Team', icon: Users, adminOnly: true },
  { key: 'settings', href: '/oncall/settings', label: 'Settings', icon: SlidersHorizontal, adminOnly: true },
];

/** Calls owns the index and every /oncall/calls/... page (the detail deep link). */
function isTabActive(tab: OncallTab, pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (tab.key === 'calls') return path === '/oncall' || path.startsWith('/oncall/calls');
  return path === tab.href || path.startsWith(`${tab.href}/`);
}

// ---------------------------------------------------------------------------
// Entitlement (copied from LmsApp's ProtectedRoute)
// ---------------------------------------------------------------------------

function Spinner({ fullScreen = true }: { fullScreen?: boolean }) {
  return (
    <div
      className={
        fullScreen
          ? 'flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900'
          : 'flex justify-center py-12'
      }
    >
      <div
        role="status"
        aria-label="Loading"
        className={`${fullScreen ? 'h-8 w-8' : 'h-6 w-6'} animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400`}
      />
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, currentSite, sitesLoading, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);
  // The site the verdict belongs to (undefined: none yet). On a site switch
  // the first render still holds the previous site's verdict; the Spinner
  // covers it so no tab fires a request for a site nobody has checked.
  const [verdictSiteId, setVerdictSiteId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    const forSite = currentSite?.id ?? null;

    const settle = (allowed: boolean) => {
      if (cancelled) return;
      setHasEntitlement(allowed);
      setEntitlementChecked(true);
      setVerdictSiteId(forSite);
      setCheckingEntitlement(false);
    };

    const checkEntitlement = async () => {
      if (isSuperAdmin) return settle(true);
      if (!currentSite) return settle(false);
      try {
        const response = await api.get('/api/my-tools');
        const tools: Array<{ tool_id?: string; is_available?: boolean }> = Array.isArray(response.data)
          ? response.data
          : [];
        settle(tools.find((t) => t.tool_id === 'oncall')?.is_available === true);
      } catch (error) {
        console.error('Error checking 24-Hour Line entitlement:', error);
        settle(false);
      }
    };

    if (session && !sitesLoading) {
      setCheckingEntitlement(true);
      checkEntitlement();
    }

    return () => {
      cancelled = true;
    };
  }, [session, isSuperAdmin, currentSite, sitesLoading]);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (sitesLoading || checkingEntitlement || verdictSiteId !== (currentSite?.id ?? null)) {
    return <Spinner />;
  }

  if (entitlementChecked && !hasEntitlement && !isSuperAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
        <div className="max-w-md p-8 text-center">
          <h1 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">Access Denied</h1>
          <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">
            You do not have access to the 24-Hour Line for your current site. Please contact your administrator if you
            believe this is an error.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 sm:min-h-0"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  // super_admin passes without a site; the module still needs one, because
  // every /api/oncall call works on the line of the site in X-Site-Id.
  if (!currentSite) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
        <EmptyState icon={Building2} title="No site selected" hint="Select a site from the header to use the 24-Hour Line." />
      </div>
    );
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// The line-admin role (plan 7.5), for showing the admin tabs only
// ---------------------------------------------------------------------------

interface OncallAdminState {
  /** True until the current site's answer (or failure) is in. */
  loading: boolean;
  isOncallAdmin: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * super_admin and platform administrators are line admins outright (no
 * wait); anyone else is one when GET /api/oncall/health says is_admin for the
 * current site (its tool_user_roles (oncall, admin) row). False while loading
 * and after a failure (the admin tabs stay hidden and the error line says so;
 * the server enforces requireOncallAdmin regardless).
 */
function useOncallAdmin(): OncallAdminState {
  const { currentSite, isAdmin } = useAuth();
  const siteId = currentSite?.id ?? null;
  const [answer, setAnswer] = useState<{ siteId: string; isAdmin: boolean; error: string | null } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (isAdmin || !siteId) return;
    let cancelled = false;
    getOncallHealth()
      .then((health) => {
        if (!cancelled) setAnswer({ siteId, isAdmin: health?.is_admin === true, error: null });
      })
      .catch((err) => {
        // `cancelled` drops a slow answer for a site that is no longer current.
        if (!cancelled) {
          setAnswer({ siteId, isAdmin: false, error: apiErrorMessage(err, 'Your 24-Hour Line role could not be loaded') });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, siteId, attempt]);

  const retry = useCallback(() => {
    setAnswer(null);
    setAttempt((n) => n + 1);
  }, []);

  if (isAdmin) return { loading: false, isOncallAdmin: true, error: null, retry };
  if (!siteId) return { loading: false, isOncallAdmin: false, error: null, retry };
  const current = answer && answer.siteId === siteId ? answer : null;
  return {
    loading: current === null,
    isOncallAdmin: current?.isAdmin === true,
    error: current?.error ?? null,
    retry,
  };
}

// ---------------------------------------------------------------------------
// Shell: header, tab nav, layout
// ---------------------------------------------------------------------------

function Header() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <AppBar
      right={
        <>
          <SiteSelector />
          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              aria-label="User menu"
              aria-expanded={userMenuOpen}
              className="flex min-h-[44px] items-center gap-1.5 rounded-lg p-1.5 text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 sm:min-h-0"
            >
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt={user.full_name || 'User'} className="h-7 w-7 rounded-full" />
              ) : (
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
                  <User size={16} aria-hidden="true" />
                </div>
              )}
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={`hidden transition-transform sm:block ${userMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.full_name || 'User'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                >
                  <LogOut size={16} aria-hidden="true" />
                  Back to Tool Hub
                </button>
              </div>
            )}
          </div>
        </>
      }
    />
  );
}

const tabCls = (active: boolean) =>
  `inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:min-h-0 sm:py-2 ${
    active
      ? 'border-primary-600 text-primary-700 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
  }`;

function TabNav({ isOncallAdmin }: { isOncallAdmin: boolean }) {
  const { pathname } = useLocation();
  const tabs = ONCALL_TABS.filter((tab) => !tab.adminOnly || isOncallAdmin);
  return (
    <nav
      className="flex flex-shrink-0 gap-1 overflow-x-auto border-b border-gray-200 px-4 dark:border-gray-700 sm:px-6 lg:px-8"
      aria-label="24-Hour Line sections"
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const active = isTabActive(tab, pathname);
        return (
          <Link
            key={tab.key}
            to={tab.href}
            aria-current={active ? 'page' : undefined}
            data-testid={`oncall-tab-${tab.key}`}
            className={tabCls(active)}
          >
            <Icon size={16} aria-hidden="true" />
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

/** When the role call fails the admin tabs stay hidden: say so and offer a retry (loud, not silent). */
function RoleErrorLine({ message, onRetry }: { message: string; onRetry: () => void }) {
  const sentence = /[.!?]$/.test(message.trim()) ? message.trim() : `${message.trim()}.`;
  return (
    <div
      role="alert"
      className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 sm:px-6 lg:px-8"
    >
      <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span className="min-w-0">{sentence} The Team and Settings tabs stay hidden until it loads.</span>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex min-h-[44px] items-center rounded-lg border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/40 sm:min-h-0"
      >
        Retry
      </button>
    </div>
  );
}

function Layout({ admin, children }: { admin: OncallAdminState; children: ReactNode }) {
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
      <Header />
      <TabNav isOncallAdmin={admin.isOncallAdmin} />
      {admin.error && <RoleErrorLine message={admin.error} onRetry={admin.retry} />}
      <main className="min-w-0 flex-1 overflow-auto">{children}</main>
      <footer className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-2 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col items-center justify-between gap-1 text-xs text-gray-500 dark:text-gray-400 sm:flex-row">
          <p>
            &copy; {COPYRIGHT_YEAR} {COMPANY_NAME}. All rights reserved.
          </p>
          <p>
            Version {APP_VERSION} &middot; {BUILD_DATE}
          </p>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

/** Waits while the role loads (so an admin's deep link or reload is not bounced), else sends a non-admin to Calls. */
function AdminOnly({ admin, children }: { admin: OncallAdminState; children: ReactNode }) {
  if (admin.loading) return <Spinner fullScreen={false} />;
  if (!admin.isOncallAdmin) return <Navigate to="/oncall" replace />;
  return <>{children}</>;
}

function OncallShell() {
  const admin = useOncallAdmin();
  return (
    <Layout admin={admin}>
      <Routes>
        <Route index element={<CallsTab />} />
        <Route path="calls/:callId" element={<CallsTab />} />
        <Route path="calls" element={<Navigate to="/oncall" replace />} />
        <Route
          path="team"
          element={
            <AdminOnly admin={admin}>
              <TeamTab />
            </AdminOnly>
          }
        />
        <Route
          path="settings"
          element={
            <AdminOnly admin={admin}>
              <SettingsTab />
            </AdminOnly>
          }
        />
        <Route path="*" element={<Navigate to="/oncall" replace />} />
      </Routes>
    </Layout>
  );
}

export function OncallApp() {
  return (
    <ProtectedRoute>
      <ToastProvider>
        <OncallShell />
      </ToastProvider>
    </ProtectedRoute>
  );
}

export default OncallApp;
